import {
  LoadingIndicatorMessage,
  NoUserActionMessage,
  UserActionRequiredMessage
} from "@sgwt-widget/components-base";
import { Component, h } from "preact";
import { UserActionOnAuthErrorMessage } from "../generic/generic-message";
import { DataStatus } from "../generic/generic-types";
import { maxSelectedClients } from "../utils/clientlistHelper";
import { generateUniqueId } from "../utils/uniqueId";
import { ApiBackedComponent } from "./ApiBackedComponent";

interface IState {
  dataStatus: DataStatus;
  correlationId: string;
  statusText?: string;
}

/**
 * preact components that interact with the API to fetch data necessary for their display
 * should extend from this class.
 */
export abstract class ApiBackedScComponent<P extends {}, S> extends Component<
  P,
  S & IState
> {
  public render() {
    let requestIdMessage = "";
    if (this.state.correlationId !== undefined) {
      requestIdMessage = ` (request id: ${this.state.correlationId.substr(
        0,
        8
      )})`;
    }

    const noUserActionMapping: { [dataStatus: string]: string } = {
      "client-request-error": ApiBackedComponent.CLIENT_ERROR,
      "no-data": "Profitability data has not been found for this selection",
      "no-data-deals":
        "Profitability data has not been found for this selection",
    };
    if (noUserActionMapping[this.state.dataStatus] !== undefined) {
      return (
        <div style="height :70%;">
          <NoUserActionMessage
            message={
              noUserActionMapping[this.state.dataStatus] + requestIdMessage
            }
          />
        </div>
      );
    }
    switch (this.state.dataStatus) {
      case "loading":
        return <LoadingIndicatorMessage />;
      case "ready":
        return this.renderWithDataReady();
      case "server-error":
        return (
          <NoUserActionMessage message={"Server error" + requestIdMessage} />
        );
      case "loading-deals":
        return (
          <div style="height :70%;">
            <LoadingIndicatorMessage />
          </div>
        );
      case "server-error-deals":
        return (
          <div style="height :70%;">
            <NoUserActionMessage message={"Server error" + requestIdMessage} />
          </div>
        );
      case "incompatible-filters-error":
        return (
          <UserActionRequiredMessage
          message={
            `${this.state.statusText} has been defined in both standard and advanced filters, and this is causing a conflict. Please update.`
          }
        />
        );
      case "no-access-rights":
      case "no-access-rights-deals":
        return <UserActionOnAuthErrorMessage message={this.state.statusText} />;

      case "too-many-clients":
        return (
          <NoUserActionMessage
            message={`Number of selected clients must be lower than ${maxSelectedClients}. Please reduce the number of clients`}
          />
        );
      default:
      /**/
    }
    return <div />;
  }

  public componentDidMount() {
    this.setDataStatus("loading");
    this.fetchApiData(this.props);
  }
  public componentWillReceiveProps(nextProps: P) {
    if (this.shouldFetchApiData(nextProps)) {
      this.fetchApiData(nextProps);
    }
  }
  public shouldComponentUpdate(
    nextProps: P,
    nextState: Readonly<S>,
    nextContext: any
  ): boolean {
    if (
      this.shouldFetchApiData(nextProps) ||
      this.state !== nextState ||
      this.context !== nextContext
    ) {
      return true;
    }
    return false;
  }

  /**
   * Return true if the nextProps indicates that the data should be fetched from the API again.
   * @param nextProps
   */
  protected shouldFetchApiData(nextProps: P): boolean {
    return true;
  }

  /**
   * Implement your data loading in this method. Make sure to call this.setDataStatus appropriately
   * during your processing, and call initCorrelationId() at the start of the function.
   */
  protected abstract fetchApiData(props: Readonly<P>): void | Promise<void>;

  /**
   * Render the content that should be shown when the data is available.
   */
  protected abstract renderWithDataReady(): JSX.Element;

  protected setDataStatus(status: DataStatus) {
    this.setState({ ...this.state, dataStatus: status });
  }
  protected setStatusText(statusText?: string) {
    this.setState({ ...this.state, statusText: statusText });
  }

  /**
   * Initializes a unique id in the state
   */
  protected initCorrelationId() {
    const correlationId = generateUniqueId();
    this.setState({ ...this.state, correlationId });
  }
}
